import { useState, useEffect } from 'react'
import s from './AnualOpportunitiesSold.scss'
import {
  makeReportsPostRequest,
  makeGetRequest,
  getReportsBaseUrl,
  makeReportsGetRequest
} from 'utils/api'
import { toUsers, getTotalValue, toYears } from './serializers'
import { useLoading } from 'hooks'
import { getCurrency, getToken } from 'utils/authService'
import MaterialTable from '@material-table/core'
import { NumericFormat } from 'react-number-format'
import { timezoneDate, currencyFormatter } from 'utils/helpers'
import Select from 'react-select'
import { FaInfoCircle, FaFileExcel } from 'react-icons/fa'
import { Card, CardBody, CardHeader, Collapse, UncontrolledTooltip } from 'reactstrap'
import currency from 'currency.js'
import { AddButton } from 'components/common/AddButton'
import { ExportCsv, ExportPdf } from '@material-table/exporters'
import { toTeams } from 'utils/serializers'

const getGroupValue = (group, data) => {
  return data.reduce((acc, row) => {
    if (row[group.field] === group.value) {
      acc += row.value
    }
    return acc
  }, 0)
}

const AnualOpportunitiesSold = () => {
  const [result, setResult] = useState([])
  const [users, setUsers] = useState([])
  const [years, setYears] = useState([])
  const [prevYears, setPrevYears] = useState(years)
  const [userFilter, setUserFilter] = useState([])
  const [yearFilter, setYearFilter] = useState(null)
  const [totalValue, setTotalValue] = useState(0)
  const [, withLoadingLocks] = useLoading()
  const [teamFilter, setTeamFilter] = useState([])
  const [teams, setTeams] = useState([])

  const columns = [
    {
      title: 'Opp Name',
      field: 'name',
      width: '200px',
      sorting: true,
      render: row => {
        return (
          <a href={`/opportunities/${row.dealId}`} target="_blank" rel="noreferrer">
            {row.name}
          </a>
        )
      },
      groupRender: row => {
        return (
          <>
            <span>{row}</span>
            {' - '}
            <NumericFormat
              thousandSeparator
              decimalScale={2}
              value={getGroupValue({ field: 'name', value: row }, result)}
              prefix={getCurrency().symbol}
              displayType="text"
            />
          </>
        )
      }
    },
    {
      title: 'Won Date',
      field: 'wonDate',
      sorting: true,
      width: '200px',
      defaultSort: 'asc',
      render: row => {
        return row.wonDate ? timezoneDate(row.wonDate) : null
      },
      groupRender: row => {
        return timezoneDate(row)
      },
      exportTransformer: row => (row.wonDate ? timezoneDate(row.wonDate) : null)
    },
    {
      title: 'Value',
      field: 'value',
      width: '200px',
      sorting: true,
      render: row => {
        return (
          <NumericFormat
            thousandSeparator
            decimalScale={2}
            value={row.value}
            prefix={getCurrency().symbol}
            displayType="text"
          />
        )
      },
      groupRender: row => {
        return (
          <NumericFormat
            thousandSeparator
            decimalScale={2}
            value={row}
            prefix={getCurrency().symbol}
            displayType="text"
          />
        )
      },
      exportTransformer: row => currencyFormatter({ value: row.value })
    },
    {
      title: 'Cost',
      field: 'cost',
      width: '200px',
      sorting: true,
      render: row => {
        return (
          <NumericFormat
            thousandSeparator
            decimalScale={2}
            value={row.cost}
            prefix={getCurrency().symbol}
            displayType="text"
          />
        )
      },
      groupRender: row => {
        return (
          <NumericFormat
            thousandSeparator
            decimalScale={2}
            value={row}
            prefix={getCurrency().symbol}
            displayType="text"
          />
        )
      },
      exportTransformer: row => currencyFormatter({ value: row.cost })
    },
    {
      title: 'Profit',
      field: 'grossProfit',
      width: '200px',
      sorting: true,
      render: row => {
        return (
          <NumericFormat
            thousandSeparator
            decimalScale={2}
            value={row.grossProfit}
            prefix={getCurrency().symbol}
            displayType="text"
          />
        )
      },
      groupRender: row => {
        return (
          <NumericFormat
            thousandSeparator
            decimalScale={2}
            value={row}
            prefix={getCurrency().symbol}
            displayType="text"
          />
        )
      },
      exportTransformer: row => currencyFormatter({ value: row.grossProfit })
    },
    {
      title: 'Awarded To',
      field: 'awardedTo',
      width: '200px',
      sorting: true
    },
    {
      title: 'Contact(s)',
      field: 'contacts',
      sorting: true,
      width: '400px'
    },
    {
      title: 'Bid Due Date',
      field: 'dueDate',
      sorting: true,
      width: '200px',
      render: row => {
        return row.dueDate ? timezoneDate(row.dueDate) : null
      },
      groupRender: row => {
        return timezoneDate(row)
      },
      exportTransformer: row => (row.dueDate ? timezoneDate(row.dueDate) : null)
    },
    {
      title: 'Bid Follow-up Date',
      field: 'followUpDate',
      sorting: true,
      width: '200px',
      render: row => {
        return row.followUpDate ? timezoneDate(row.followUpDate) : null
      },
      groupRender: row => {
        return timezoneDate(row)
      },
      exportTransformer: row => (row.followUpDate ? timezoneDate(row.followUpDate) : null)
    },
    {
      title: 'Opp Manager',
      field: 'manager',
      width: '200px',
      sorting: true
    },
    {
      title: 'Team(s)',
      field: 'teams',
      width: '250px',
      sorting: true,
      render: row => row.teams?.join(', '),
      exportTransformer: row => row.teams?.join(', ')
    },
    {
      title: 'Days to Close',
      field: 'daysToClose',
      width: '200px',
      sorting: true
    },
    {
      title: 'Quote #',
      field: 'quoteNumber',
      width: '200px',
      sorting: true
    },
    {
      title: 'Project #',
      field: 'projectNumber',
      width: '200px',
      sorting: true
    }
  ]

  useEffect(() => {
    ;(async () => {
      const { content } = await makeGetRequest('/v1/users')
      setUsers(toUsers(content))

      const years = await makeReportsGetRequest('/v1/sales/ytd/available_years')
      setYears(toYears(years))

      const teams = await makeGetRequest('/v1/teams')
      setTeams(toTeams(teams))
    })()
  }, [])

  if (prevYears !== years) {
    setPrevYears(years)
    setYearFilter(years[years.length - 1])
  }

  useEffect(() => {
    if (!yearFilter) return
    ;(async () => {
      const result = await withLoadingLocks(
        makeReportsPostRequest('/v1/sales/ytd/oppt/sold', {
          ytd: yearFilter?.value ?? new Date().getFullYear(),
          ...(userFilter && userFilter.length
            ? { userFilter: userFilter.map(user => user.value) }
            : {}),
          ...(teamFilter && teamFilter.length
            ? { teamFilter: teamFilter.map(user => user.value) }
            : {})
        })
      )
      setResult(result)
      setTotalValue(getTotalValue(result))
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilter, yearFilter, teamFilter])

  const onBtnExport = () => {
    window.open(
      getReportsBaseUrl(
        `/v1/sales/ytd/oppt/sold/export?ytd=${yearFilter.value}&userFilter=${userFilter
          .map(user => user.value)
          .join()}&teamFilter=${teamFilter.map(user => user.value).join()}&token=${getToken()}`
      ),
      '_blank'
    )
  }

  return (
    <>
      <h6 className={s.title}>Sales Reports &gt; YTD + Anual Opportunities Sold</h6>
      <div className={s.filters}>
        <Select
          onChange={values => setYearFilter(values)}
          options={years}
          value={yearFilter}
          placeholder="Year to Date (YTD)"
          className={s.userSelect}
        />
        <Select
          onChange={values => setUserFilter(values)}
          options={users}
          value={userFilter}
          placeholder="Select User(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type1" />
        <UncontrolledTooltip placement="bottom" target="Type1">
          To see all opportunities, please remove all users from selection.
        </UncontrolledTooltip>
        <Select
          onChange={values => setTeamFilter(values)}
          options={teams}
          value={teamFilter}
          placeholder="Select Team(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type2" />
        <UncontrolledTooltip placement="bottom" target="Type2">
          To see all opportunities, please remove all teams from selection.
        </UncontrolledTooltip>
      </div>
      {result.length > 0 ? (
        <>
          <div className={s.table}>
            <AddButton className={s.exportExcel} onClick={onBtnExport}>
              <FaFileExcel className={s.excelIcon} />
              Export to Excel
            </AddButton>
            <Card className={s.card}>
              <CardHeader>
                Opportunities Sold Total ({result.length}) &#8226;
                {currency(totalValue, {
                  symbol: getCurrency().symbol
                }).format()}{' '}
              </CardHeader>
              <Collapse isOpen={true}>
                <CardBody>
                  <MaterialTable
                    title=""
                    columns={columns}
                    data={result}
                    options={{
                      tableLayout: 'fixed',
                      sorting: true,
                      grouping: true,
                      headerStyle: {
                        backgroundColor: '#eee',
                        color: '#000',
                        fontSize: '14px'
                      },
                      pageSize: 10,
                      padding: 'dense',
                      pageSizeOptions: [5, 10, 20, 50, { value: result.length, label: 'All' }],
                      emptyRowsWhenPaging: false,
                      exportMenu: [
                        {
                          label: 'Export PDF',
                          exportFunc: (cols, datas) => {
                            ExportPdf(cols, datas, 'YTD + Anual Opportunities Sold')
                          }
                        },
                        {
                          label: 'Export CSV',
                          exportFunc: (cols, datas) =>
                            ExportCsv(cols, datas, 'YTD + Anual Opportunities Sold')
                        }
                      ],
                      columnsButton: true
                    }}
                    renderSummaryRow={({ column, data }) =>
                      column.field === 'value'
                        ? {
                            value: currencyFormatter({
                              value: data.reduce((agg, row) => agg + row.value, 0)
                            }),
                            style: { background: '#ccc' }
                          }
                        : undefined
                    }
                    style={{
                      boxShadow: 'none',
                      fontSize: '13px'
                    }}
                  />
                </CardBody>
              </Collapse>
            </Card>
          </div>
        </>
      ) : (
        <p className={s.noResults}>No results to display. Try different filters.</p>
      )}
    </>
  )
}

export default AnualOpportunitiesSold
